import type {UseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, skipToken, useQuery} from '@tanstack/react-query'

import type {SupportedLocales} from '@/lib/locale/locale'
import type {Order} from '@/models/orderModel'
import {confirmOrder} from '@/services/orderService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getCheckOutOrderQueryKey(params: {locale: SupportedLocales; orderId?: string}) {
  return ['checkOutOrder', params.locale, params.orderId] as const
}

export function getCheckOutOrderQueryOptions<TData = Order | false | string, TError = Error>(
  params: {
    locale: SupportedLocales
    orderId?: string
  },
  options?: Omit<UseQueryOptions<Order | false | string, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return queryOptions({
    queryKey: getCheckOutOrderQueryKey(params),
    queryFn: params.orderId
      ? async ({signal}) => {
          invariant(params.orderId, 'orderId is required')
          return confirmOrder({
            locale: params.locale,
            orderId: params.orderId,
            signal,
          })
        }
      : skipToken,
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

export default function useCheckoutOrder<TData = Order | false | string, TError = Error>(
  params: {locale: SupportedLocales; orderId?: string},
  options?: Omit<UseQueryOptions<Order | false | string, TError, TData>, 'queryKey' | 'queryFn'>,
) {
  return useQuery(getCheckOutOrderQueryOptions(params, options))
}
